import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { api, ApiError } from "../../lib/api";
import { fmtMoney } from "../../lib/format";
import type { ConnectedServiceDTO, SubscriptionDTO } from "@shared/api";

interface Props {
  sub: SubscriptionDTO;
  services: ConnectedServiceDTO[];
  termMonths: number;
  onSaved: () => void | Promise<void>;
}

export function ServicesConfigurator({
  sub,
  services,
  termMonths,
  onSaved,
}: Props) {
  const [enabled, setEnabled] = useState<string[]>(sub.services);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(sub.services);
  }, [sub.services]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const s of services) set.add(s.category);
    return [...set];
  }, [services]);

  const monthlyTotal = useMemo(
    () =>
      enabled.reduce((sum, id) => {
        const s = services.find((c) => c.id === id);
        return sum + (s?.monthly ?? 0);
      }, 0),
    [enabled, services],
  );

  const dirty = useMemo(() => {
    const a = [...enabled].sort();
    const b = [...sub.services].sort();
    return a.length !== b.length || a.some((x, i) => x !== b[i]);
  }, [enabled, sub.services]);

  const toggle = (id: string) =>
    setEnabled((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  async function apply() {
    setSaving(true);
    setError(null);
    try {
      await api.subscriptions.setServices(sub.id, enabled);
      await onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bbc-config">
      <div className="bbc-config-main">
        {error && <div className="bbc-error">{error}</div>}
        {categories.map((cat) => {
          const inCat = services.filter((s) => s.category === cat);
          const enabledCount = inCat.filter((s) => enabled.includes(s.id))
            .length;
          return (
            <div key={cat} className="bbc-config-section">
              <div className="bbc-config-section-head">
                <h4>{cat}</h4>
                <span className="bbc-config-section-count mono">
                  {enabledCount}
                  <span className="dim"> / {inCat.length}</span>
                </span>
              </div>
              <div className="bbc-svc-grid">
                {inCat.map((s) => {
                  const on = enabled.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`bbc-svc-card ${on ? "is-on" : ""}`}
                      onClick={() => toggle(s.id)}
                    >
                      <div className="bbc-svc-card-head">
                        <span className="bbc-svc-cat mono">
                          {s.category.toUpperCase()}
                        </span>
                        <span
                          className={`bbc-svc-check ${on ? "is-on" : ""}`}
                        >
                          {on ? (
                            <svg viewBox="0 0 14 14" width="14" height="14">
                              <path
                                d="M3 7.2 5.8 10 11 4.2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            "+"
                          )}
                        </span>
                      </div>
                      <div className="bbc-svc-name">{s.name}</div>
                      <div className="bbc-svc-desc">{s.description}</div>
                      <div className="bbc-svc-foot">
                        <span className="mono">
                          {fmtMoney(s.monthly)}
                          <span className="dim">/mo</span>
                        </span>
                        <span className="bbc-svc-metric">{s.metric}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <aside className="bbc-config-aside">
        <Card title="Configuration summary">
          <div className="bbc-sum-row">
            <span>Services enabled</span>
            <span className="mono">{enabled.length}</span>
          </div>
          <div className="bbc-sum-row">
            <span>Monthly commitment</span>
            <span className="mono">{fmtMoney(monthlyTotal)}</span>
          </div>
          <div className="bbc-sum-row">
            <span>Term length</span>
            <span className="mono">{termMonths} mo</span>
          </div>
          <div className="bbc-sum-row bbc-sum-row-total">
            <span>Term total</span>
            <span className="mono lg">
              {fmtMoney(monthlyTotal * termMonths)}
            </span>
          </div>
          <ul className="bbc-sum-list">
            {enabled.map((id) => {
              const s = services.find((c) => c.id === id);
              return s ? (
                <li key={id}>
                  <span>{s.name}</span>
                  <span className="mono">{fmtMoney(s.monthly)}</span>
                </li>
              ) : null;
            })}
          </ul>
          <div className="bbc-sum-actions">
            <Button
              variant="primary"
              onClick={apply}
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Apply changes"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setEnabled(sub.services)}
              disabled={!dirty || saving}
            >
              Discard
            </Button>
          </div>
          <div className="bbc-sum-note">
            Changes take effect at the next billing cycle. IP-scope amendments
            may require legal review.
          </div>
        </Card>
      </aside>
    </div>
  );
}
